import classNames from 'classnames';
import { createElement, JSX, type ReactNode } from 'react';
import atTextVariants from './at-text.variants';
import {
  TAtTextSupportedElements,
  ETextVariants,
  ETextSize,
} from './at-text.types';

export interface IAtTextProps {
  children?: ReactNode;
  type?: TAtTextSupportedElements;
  className?: string;
  variant?: ETextVariants;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  'data-testid'?: string;
  id?: string;
}

/**
 * `AtText` is a flexible, semantic text component that renders any supported inline/block HTML tag
 * (like `p`, `span`, or `strong`) with consistent styling via Tailwind Variants.
 *
 * This component is used for all non-heading body copy in the system and supports
 * customizable sizing, variants, and semantic tags.
 *
 */

export const AtText = ({
  children,
  type = 'p',
  className,
  size = ETextSize.LG,
  variant = ETextVariants.DEFAULT,
  'data-testid': dataTestId,
  id,
}: IAtTextProps): JSX.Element => {
  const jsxElement = createElement(
    type,
    {
      id,
      className: classNames(
        atTextVariants({ elementType: type, size, variant }),
        className,
      ),
      'data-testid': dataTestId || 'at-text',
    },
    children,
  );

  return jsxElement;
};
